import { Alert, Button, DatePicker, Drawer, Form, Grid, Input, Select, Space } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { type Dayjs } from "dayjs";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError, errorLines } from "../app/problems.ts";

type AcademicYear = components["schemas"]["AcademicYearDetails"];
type DayOfWeek = components["schemas"]["DayOfWeek"];

const weekDays: DayOfWeek[] = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

interface YearForm {
  label: string;
  startDate: Dayjs;
  endDate: Dayjs;
  weeklyDayOff: DayOfWeek;
}

interface Props {
  open: boolean;
  /** Undefined creates a new year; a year edits it. */
  year?: AcademicYear | undefined;
  onClose(): void;
  onSaved(year: AcademicYear, created: boolean): void;
}

export function AcademicYearDrawer({ open, year, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<YearForm>();
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: async (values: YearForm) => {
      const body = {
        label: values.label.trim(),
        startDate: values.startDate.format("YYYY-MM-DD"),
        endDate: values.endDate.format("YYYY-MM-DD"),
        weeklyDayOff: values.weeklyDayOff,
      };
      const result = year
        ? await api.PUT("/academic/years/{id}", { params: { path: { id: year.id } }, body })
        : await api.POST("/academic/years", { body });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["academic"] });
      setServerErrors([]);
      onSaved(saved, !year);
    },
    onError: (err) => setServerErrors(errorLines(err, t("app.error"))),
  });

  const initialValues: Partial<YearForm> = year
    ? { label: year.label, startDate: dayjs(year.startDate), endDate: dayjs(year.endDate), weeklyDayOff: year.weeklyDayOff }
    : { weeklyDayOff: "Sunday" };

  return (
    <Drawer
      title={year ? t("academicYears.edit") : t("academicYears.create")}
      open={open}
      onClose={onClose}
      size={screens.md ? 480 : "100%"}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="primary" loading={mutation.isPending} onClick={() => form.submit()}>
            {t("common.save")}
          </Button>
        </Space>
      }
    >
      {serverErrors.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} role="alert" title={serverErrors.map((m) => <div key={m}>{m}</div>)} />
      )}
      <Form<YearForm> form={form} layout="vertical" requiredMark={false} initialValues={initialValues} onFinish={(values) => mutation.mutate(values)}>
        <Form.Item name="label" label={t("academicYears.label")} extra={t("academicYears.labelHint")} rules={[{ required: true, message: t("common.required") }]}>
          <Input maxLength={32} />
        </Form.Item>
        <div style={{ display: "grid", gridTemplateColumns: screens.sm ? "1fr 1fr" : "1fr", gap: 12 }}>
          <Form.Item name="startDate" label={t("academicYears.startDate")} rules={[{ required: true, message: t("common.required") }]}>
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="endDate" label={t("academicYears.endDate")} rules={[{ required: true, message: t("common.required") }]}>
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
          </Form.Item>
        </div>
        <Form.Item name="weeklyDayOff" label={t("academicYears.weeklyDayOff")}>
          <Select options={weekDays.map((d) => ({ value: d, label: t(`academicYears.days.${d}`) }))} />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
